import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Accessibility,
  Search,
  Bell,
  Zap,
  HelpCircle,
  Command,
  SkipForward,
} from 'lucide-react';
import { prefersReducedMotion } from '@/lib/a11y';

interface AccessibilityHelpersProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface KeyboardShortcut {
  keys: string[];
  description: string;
  category: string;
}

const SHORTCUTS: KeyboardShortcut[] = [
  {
    keys: ['?'],
    description: 'Open this help dialog',
    category: 'General'
  },
  {
    keys: ['/'],
    description: 'Focus global search',
    category: 'Navigation'
  },
  {
    keys: ['Ctrl', 'Shift', 'N'],
    description: 'Open notifications',
    category: 'Navigation'
  },
  {
    keys: ['Shift', 'A'],
    description: 'Open quick actions',
    category: 'Actions'
  },
  {
    keys: ['Ctrl', 'K'],
    description: 'Open command palette',
    category: 'Navigation'
  },
  {
    keys: ['Ctrl', '/'],
    description: 'Open church switcher',
    category: 'Navigation'
  },
  {
    keys: ['Escape'],
    description: 'Close any open modal or drawer',
    category: 'General'
  },
  {
    keys: ['Tab'],
    description: 'Navigate through interactive elements',
    category: 'Navigation'
  }
];

const CATEGORIES = ['General', 'Navigation', 'Actions'];

export function AccessibilityHelpers({ open, onOpenChange }: AccessibilityHelpersProps) {
  const skipContentRef = useRef<HTMLAnchorElement>(null);

  // Keyboard navigation handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Help dialog trigger
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        onOpenChange(true);
      }
      
      // Focus global search
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        const searchElement = document.querySelector('[data-global-search]') as HTMLElement;
        if (searchElement) {
          searchElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onOpenChange]);

  const renderKeyCombo = (keys: string[]) => {
    return (
      <div className="flex items-center gap-1">
        {keys.map((key, index) => (
          <React.Fragment key={key}>
            <kbd className="px-2 py-1 text-xs font-mono bg-muted border rounded">
              {key}
            </kbd>
            {index < keys.length - 1 && <span className="text-muted-foreground">+</span>}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <>
      {/* Skip to content link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only fixed top-2 left-2 z-[9999] rounded bg-primary px-3 py-2 text-primary-foreground"
      >
        Skip to main content
      </a>

      {/* Help Dialog */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent 
          className="max-w-2xl max-h-[80vh] overflow-y-auto"
          aria-describedby={undefined}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Accessibility className="h-5 w-5" />
              Accessibility & Keyboard Shortcuts
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Accessibility Features */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <SkipForward className="h-4 w-4" />
                Accessibility Features
              </h3>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>• Skip to content link available (focus with Tab)</p>
                <p>• All interactive elements are keyboard accessible</p>
                <p>• Screen reader support with proper ARIA labels</p>
                <p>• High contrast focus indicators on all controls</p>
                <p>• Semantic HTML structure for better navigation</p>
              </div>
            </section>

            {/* Keyboard Shortcuts */}
            <section>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Command className="h-4 w-4" />
                Keyboard Shortcuts
              </h3>
              
              <ul className="space-y-2">
                <li><kbd className="inline px-2 py-1 text-xs font-mono bg-muted border rounded">/</kbd> Focus global search</li>
                <li><kbd className="inline px-2 py-1 text-xs font-mono bg-muted border rounded">⌘K</kbd>/<kbd className="inline px-2 py-1 text-xs font-mono bg-muted border rounded">Ctrl K</kbd> Command palette</li>
                <li><kbd className="inline px-2 py-1 text-xs font-mono bg-muted border rounded">Ctrl ⇧ N</kbd> Notifications</li>
                <li><kbd className="inline px-2 py-1 text-xs font-mono bg-muted border rounded">⇧ A</kbd> Quick actions</li>
                <li><kbd className="inline px-2 py-1 text-xs font-mono bg-muted border rounded">Esc</kbd> Close modals/drawers</li>
                <li><kbd className="inline px-2 py-1 text-xs font-mono bg-muted border rounded">?</kbd> Open this help</li>
              </ul>
            </section>

            {/* Usage Tips */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <HelpCircle className="h-4 w-4" />
                Usage Tips
              </h3>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>• Use Tab to navigate through all interactive elements</p>
                <p>• Press Enter or Space to activate buttons and links</p>
                <p>• Use arrow keys to navigate within menus and lists</p>
                <p>• Press Escape to close any open dialog or drawer</p>
                <p>• Screen readers announce sections and landmarks automatically</p>
              </div>
            </section>
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={() => onOpenChange(false)}>
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Hook for managing global accessibility state
export function useAccessibility() {
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC closes any open modal/drawer
      if (e.key === 'Escape') {
        setShowHelp(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return {
    showHelp,
    setShowHelp,
    openHelp: () => setShowHelp(true),
    closeHelp: () => setShowHelp(false)
  };
}
